/**
 * MacroLibraryPage — full macro library reference + playground (feature #175).
 *
 * Documentation-style page. Two halves:
 *   1. Token reference — grouped by category, shows token, description,
 *      example, and URL-encoded snippet. Read-only.
 *   2. Interactive playground — operator pastes a URL template + a sample
 *      context (or uses the shorthand parser), clicks "Preview", and sees
 *      the server render, substituted list, empty list, unknowns, and any
 *      validation warnings or errors.
 *
 * Brand: emerald -> sky gradient heading, zinc borders, 6-8 px radii, light
 * mode only. No emojis. No competitor naming.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
	MACRO_CATEGORIES,
	MACRO_CATEGORY_HINT,
	MACRO_CATEGORY_LABEL,
	type MacroCategory,
	type MacroContext,
	type MacroTokenSpec,
	type PreviewResponse,
	extractPlaceholders,
	fetchMacroTokens,
	groupTokensByCategory,
	macroLibraryPaths,
	parseContextShorthand,
	previewTemplate,
	renderEncodedSnippet,
} from "./api";

// ---------------------------------------------------------------------------
// Tailwind recipe constants
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const TEXTAREA = `${INPUT} min-h-[6rem] font-mono text-xs`;
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const CHIP_ACTIVE =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white";
const CHIP_IDLE =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50";

export const CATEGORY_ENTRIES = MACRO_CATEGORIES.map((c) => ({
	key: c,
	label: MACRO_CATEGORY_LABEL[c],
	hint: MACRO_CATEGORY_HINT[c],
}));

// ---------------------------------------------------------------------------
// Props + root
// ---------------------------------------------------------------------------

export interface MacroLibraryPageProps {
	initialCategory?: MacroCategory;
	initialTemplate?: string;
}

export function MacroLibraryPage(props: MacroLibraryPageProps = {}) {
	const [filter, setFilter] = useState<MacroCategory | "all">(
		props.initialCategory ?? "all",
	);
	const [tokens, setTokens] = useState<MacroTokenSpec[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	const reload = useCallback(async () => {
		setLoading(true);
		setLoadError(null);
		try {
			const rows = await fetchMacroTokens();
			setTokens(rows);
		} catch (err) {
			setLoadError(
				err instanceof Error ? err.message : "Could not load tokens.",
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void reload();
	}, [reload]);

	const visible = useMemo(() => {
		if (filter === "all") return tokens;
		return tokens.filter((t) => t.category === filter);
	}, [filter, tokens]);

	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14">
			<Heading />
			<CategoryFilter value={filter} onChange={setFilter} />
			<TokenReference
				tokens={visible}
				loading={loading}
				error={loadError}
			/>
			<Playground
				tokens={tokens}
				initialTemplate={props.initialTemplate}
			/>
			<SecurityRules />
		</main>
	);
}

export default MacroLibraryPage;

// ---------------------------------------------------------------------------
// Heading
// ---------------------------------------------------------------------------

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Tracking
			</p>
			<h1 className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-sky-500 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
				Macro library
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Every token Trackerity emits in an impression, click, or postback URL.
				Reference below, playground at the bottom. Values are always URL-encoded
				before they reach the wire, and templates using disallowed schemes or
				literal HTML are refused.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Category filter
// ---------------------------------------------------------------------------

function CategoryFilter(props: {
	value: MacroCategory | "all";
	onChange: (v: MacroCategory | "all") => void;
}) {
	return (
		<section
			aria-label="Category filter"
			className={`${CARD} flex flex-col gap-3 p-5`}
		>
			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					className={props.value === "all" ? CHIP_ACTIVE : CHIP_IDLE}
					onClick={() => props.onChange("all")}
					aria-pressed={props.value === "all"}
					data-testid="ml-cat-chip-all"
				>
					All categories
				</button>
				{CATEGORY_ENTRIES.map((entry) => (
					<button
						key={entry.key}
						type="button"
						className={props.value === entry.key ? CHIP_ACTIVE : CHIP_IDLE}
						onClick={() => props.onChange(entry.key)}
						aria-pressed={props.value === entry.key}
						data-testid={`ml-cat-chip-${entry.key}`}
					>
						{entry.label}
					</button>
				))}
			</div>
			<p className={HINT}>
				Narrow the reference below to a single category. "Sub" holds sub1..sub10,
				"Custom" holds {`{custom.<name>}`}.
			</p>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Token reference
// ---------------------------------------------------------------------------

function TokenReference(props: {
	tokens: MacroTokenSpec[];
	loading: boolean;
	error: string | null;
}) {
	const groups = useMemo(
		() => groupTokensByCategory(props.tokens),
		[props.tokens],
	);
	return (
		<section aria-label="Token reference" className={`${CARD} flex flex-col gap-5 p-5`}>
			<header className="flex flex-col gap-1">
				<h2 className="text-base font-semibold text-zinc-900">
					Token reference ({props.tokens.length})
				</h2>
				<p className={HINT}>
					Paste {`{token}`} into any URL template. Missing values render as an
					empty string; unknown tokens are left literal so you can layer click
					and conversion renders.
				</p>
			</header>
			{props.loading ? <p className={HINT}>Loading catalogue...</p> : null}
			{props.error ? <p className={ERROR_TEXT}>{props.error}</p> : null}
			{!props.loading && props.tokens.length === 0 ? (
				<p className={HINT}>No tokens match this filter.</p>
			) : null}
			<div className="flex flex-col gap-6">
				{groups.map((g) => (
					<CategoryBlock
						key={g.category}
						category={g.category}
						tokens={g.tokens}
					/>
				))}
			</div>
		</section>
	);
}

function CategoryBlock(props: {
	category: MacroCategory;
	tokens: MacroTokenSpec[];
}) {
	return (
		<section
			aria-label={`Category ${props.category}`}
			className="flex flex-col gap-3"
			data-testid={`ml-cat-${props.category}`}
		>
			<header className="flex flex-col gap-0.5">
				<h3 className="text-sm font-semibold text-zinc-900">
					{MACRO_CATEGORY_LABEL[props.category]}
				</h3>
				<p className={HINT}>{MACRO_CATEGORY_HINT[props.category]}</p>
			</header>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[640px] border-collapse text-left text-sm">
					<thead>
						<tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
							<th className="px-3 py-2 font-medium">Token</th>
							<th className="px-3 py-2 font-medium">Description</th>
							<th className="px-3 py-2 font-medium">Example</th>
							<th className="px-3 py-2 font-medium">URL-encoded</th>
						</tr>
					</thead>
					<tbody>
						{props.tokens.map((row) => (
							<tr
								key={row.token}
								className="border-b border-zinc-100 hover:bg-zinc-50"
								data-testid={`ml-row-${row.token}`}
							>
								<td className="px-3 py-3">
									<code className="font-mono text-xs font-semibold text-emerald-700">
										{`{${row.token}}`}
									</code>
								</td>
								<td className="px-3 py-3 text-zinc-700">{row.description}</td>
								<td className="px-3 py-3 font-mono text-xs text-zinc-600">
									{row.example}
								</td>
								<td className="px-3 py-3 font-mono text-xs text-zinc-500">
									{renderEncodedSnippet(row.token)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Playground
// ---------------------------------------------------------------------------

const DEFAULT_TEMPLATE =
	"https://adv.example/cb?c={click_id}&cid={campaign_id}&cur={currency}&rev={revenue_cents}&s1={sub1}";
const DEFAULT_CONTEXT =
	"click_id=clk_01HFR;campaign_id=cmp_spring;currency=USD;revenue_cents=4999;sub1=hero";

function Playground(props: {
	tokens: MacroTokenSpec[];
	initialTemplate?: string;
}) {
	const [template, setTemplate] = useState<string>(
		props.initialTemplate ?? DEFAULT_TEMPLATE,
	);
	const [shorthand, setShorthand] = useState<string>(DEFAULT_CONTEXT);
	const [busy, setBusy] = useState(false);
	const [result, setResult] = useState<PreviewResponse | null>(null);
	const [error, setError] = useState<string | null>(null);

	const parsedContext = useMemo<{
		context: MacroContext;
		unknown: string[];
	}>(() => parseContextShorthand(shorthand), [shorthand]);

	const localPlaceholders = useMemo(
		() => extractPlaceholders(template),
		[template],
	);

	const runPreview = useCallback(async () => {
		setBusy(true);
		setError(null);
		try {
			const res = await previewTemplate(template, parsedContext.context);
			setResult(res);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Preview failed.");
		} finally {
			setBusy(false);
		}
	}, [template, parsedContext.context]);

	return (
		<section aria-label="Playground" className={`${CARD} flex flex-col gap-4 p-5`}>
			<header className="flex flex-col gap-1">
				<h2 className="text-base font-semibold text-zinc-900">Playground</h2>
				<p className={HINT}>
					Compose a URL template and a sample context, then POST to{" "}
					<code className="rounded bg-zinc-100 px-1 py-0.5 text-zinc-700">
						{macroLibraryPaths.preview}
					</code>{" "}
					to get back the URL-encoded rendering. Templates using disallowed
					schemes are rejected and the server will NOT splice values into them.
				</p>
			</header>
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div className="flex flex-col gap-1.5">
					<label htmlFor="ml-template" className={LABEL}>
						Template
					</label>
					<textarea
						id="ml-template"
						className={TEXTAREA}
						value={template}
						onChange={(e) => setTemplate(e.currentTarget.value)}
						data-testid="ml-template"
					/>
					<p className={HINT}>
						Placeholders detected:{" "}
						{localPlaceholders.length === 0 ? (
							<span className="text-zinc-400">none</span>
						) : (
							localPlaceholders.map((p) => `{${p}}`).join(", ")
						)}
					</p>
				</div>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="ml-context" className={LABEL}>
						Context (key=value;key=value)
					</label>
					<textarea
						id="ml-context"
						className={TEXTAREA}
						value={shorthand}
						onChange={(e) => setShorthand(e.currentTarget.value)}
						data-testid="ml-context"
					/>
					<p className={HINT}>
						Use <code className="rounded bg-zinc-100 px-1 py-0.5">custom.foo=bar</code>{" "}
						for custom tokens, <code className="rounded bg-zinc-100 px-1 py-0.5">sub3=...</code>{" "}
						for publisher sub-ids.
					</p>
					{parsedContext.unknown.length > 0 ? (
						<p className={ERROR_TEXT}>
							Ignored unknown keys: {parsedContext.unknown.join(", ")}
						</p>
					) : null}
				</div>
			</div>
			<div className="flex items-center justify-between gap-3">
				<p className={HINT}>
					POST{" "}
					<code className="rounded bg-zinc-100 px-1 py-0.5 text-zinc-700">
						{macroLibraryPaths.preview}
					</code>
				</p>
				<button
					type="button"
					className={BTN_PRIMARY}
					onClick={runPreview}
					disabled={busy}
					data-testid="ml-preview-button"
				>
					{busy ? "Previewing..." : "Preview"}
				</button>
			</div>
			{error ? <p className={ERROR_TEXT}>{error}</p> : null}
			{result ? <PreviewPanel result={result} /> : null}
		</section>
	);
}

function PreviewPanel(props: { result: PreviewResponse }) {
	const r = props.result;
	return (
		<div className="flex flex-col gap-3 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
			<div>
				<p className="text-xs font-semibold text-zinc-700">Rendered URL</p>
				<pre
					className="mt-1 whitespace-pre-wrap break-all font-mono text-xs text-zinc-800"
					data-testid="ml-preview-output"
				>
					{r.output}
				</pre>
			</div>
			{r.issues.length > 0 ? (
				<div className="rounded border border-red-200 bg-red-50 p-2">
					<p className="text-xs font-semibold text-red-700">Refused</p>
					<ul className="mt-1 list-disc pl-4 text-xs text-red-800">
						{r.issues.map((i) => (
							<li key={`${i.code}-${i.detail ?? ""}`}>{i.message}</li>
						))}
					</ul>
				</div>
			) : null}
			{r.warnings.length > 0 ? (
				<div className="rounded border border-amber-200 bg-amber-50 p-2">
					<p className="text-xs font-semibold text-amber-700">Warnings</p>
					<ul className="mt-1 list-disc pl-4 text-xs text-amber-800">
						{r.warnings.map((w) => (
							<li key={`${w.code}-${w.detail ?? ""}`}>{w.message}</li>
						))}
					</ul>
				</div>
			) : null}
			<div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
				<PlaceholderList label="Substituted" items={r.substituted} tone="emerald" />
				<PlaceholderList label="Empty" items={r.empty} tone="zinc" />
				<PlaceholderList label="Unknown" items={r.unknown} tone="red" />
			</div>
		</div>
	);
}

function PlaceholderList(props: {
	label: string;
	items: string[];
	tone: "emerald" | "red" | "zinc";
}) {
	const bgClass =
		props.tone === "emerald"
			? "border-emerald-200 bg-emerald-50 text-emerald-700"
			: props.tone === "red"
				? "border-red-200 bg-red-50 text-red-700"
				: "border-zinc-200 bg-zinc-100 text-zinc-700";
	return (
		<div>
			<p className="text-xs font-semibold text-zinc-700">{props.label}</p>
			{props.items.length === 0 ? (
				<p className={HINT}>None.</p>
			) : (
				<ul className="mt-1 flex flex-wrap gap-1">
					{props.items.map((p) => (
						<li
							key={p}
							className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-mono ${bgClass}`}
						>{`{${p}}`}</li>
					))}
				</ul>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Security rules
// ---------------------------------------------------------------------------

function SecurityRules() {
	return (
		<section aria-label="Security rules" className={`${CARD} flex flex-col gap-3 p-5`}>
			<h2 className="text-base font-semibold text-zinc-900">Security rules</h2>
			<ul className="list-disc pl-5 text-sm text-zinc-700">
				<li>
					Only <code className="rounded bg-zinc-100 px-1 py-0.5">http://</code>{" "}
					and <code className="rounded bg-zinc-100 px-1 py-0.5">https://</code>{" "}
					schemes are accepted. Templates beginning with{" "}
					<code className="rounded bg-zinc-100 px-1 py-0.5">javascript:</code>,{" "}
					<code className="rounded bg-zinc-100 px-1 py-0.5">data:</code>,{" "}
					<code className="rounded bg-zinc-100 px-1 py-0.5">file:</code>,{" "}
					<code className="rounded bg-zinc-100 px-1 py-0.5">vbscript:</code>, etc.
					are refused.
				</li>
				<li>
					Every substituted value is URL-encoded. A malicious <em>value</em>{" "}
					cannot reach the raw URL stream.
				</li>
				<li>
					Templates containing a literal <code>&lt;script&gt;</code>,{" "}
					<code>&lt;iframe&gt;</code>, <code>&lt;img&gt;</code>, or an HTML
					event-handler attribute (<code>onerror=</code>, <code>onclick=</code>,
					...) are refused outright.
				</li>
				<li>
					Unknown tokens are left literal (warned, not errored) so you can
					layer click and conversion renders.
				</li>
				<li>
					<code className="rounded bg-zinc-100 px-1 py-0.5">{"{sub1}"}</code>..
					<code className="rounded bg-zinc-100 px-1 py-0.5">{"{sub10}"}</code>{" "}
					resolve against the context <code>sub</code> array; order is
					deterministic.
				</li>
			</ul>
		</section>
	);
}
